import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useAuthStore } from '@/stores/authStore';
import { submitFeatureRequest, searchSimilarFeatureRequests, complementFeatureRequest } from '@/services/data/featureRequests';
import { toast } from 'sonner';
import { Loader2, Sparkles, AlertTriangle, ArrowUp, Minus, Search, Plus, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface FeatureRequestModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly currentView: string | null;
  readonly onCreated?: () => void;
}

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Pas urgent', description: 'Peut attendre', icon: Minus, color: 'text-muted-foreground' },
  { value: 'medium', label: 'Normal', description: 'À planifier', icon: ArrowUp, color: 'text-primary' },
  { value: 'high', label: 'Urgent', description: 'Prioritaire', icon: AlertTriangle, color: 'text-warning' },
] as const;

interface SimilarIdea {
  id: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  created_at: string;
}

type ModalStep = 'form' | 'duplicates';

export function FeatureRequestModal({ open, onOpenChange, currentView, onCreated }: FeatureRequestModalProps) {
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<string>('medium');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [step, setStep] = useState<ModalStep>('form');
  const [similarIdeas, setSimilarIdeas] = useState<SimilarIdea[]>([]);
  const [selectedComplementId, setSelectedComplementId] = useState<string | null>(null);

  const { userId } = useAuthStore();

  const resetState = () => {
    setDescription('');
    setPriority('medium');
    setStep('form');
    setSimilarIdeas([]);
    setSelectedComplementId(null);
  };

  const handleCheckAndSubmit = async () => {
    if (!description.trim() || !userId) return;

    setIsSearching(true);
    const result = await searchSimilarFeatureRequests(description.trim());
    setIsSearching(false);

    if (result.success && result.data && result.data.length > 0) {
      setSimilarIdeas(result.data);
      setStep('duplicates');
    } else {
      await doSubmit();
    }
  };

  const doSubmit = async () => {
    if (!description.trim() || !userId) return;

    setIsSubmitting(true);
    const result = await submitFeatureRequest({
      reporter_id: userId,
      title: description.trim().length <= 60 ? description.trim() : description.trim().substring(0, 57) + '…',
      description: description.trim(),
      priority,
      page_url: globalThis.location.href,
      current_view: currentView,
    });
    setIsSubmitting(false);

    if (result.success) {
      toast.success('Idée ajoutée au sandbox !');
      resetState();
      onCreated?.();
      onOpenChange(false);
    } else {
      toast.error("Erreur lors de l'envoi.");
    }
  };

  const handleComplement = async (ideaId: string) => {
    if (!description.trim()) return;

    setIsSubmitting(true);
    const result = await complementFeatureRequest(ideaId, description.trim());
    setIsSubmitting(false);

    if (result.success) {
      toast.success('Idée complétée avec vos éléments !');
      resetState();
      onCreated?.();
      onOpenChange(false);
    } else {
      toast.error("Erreur lors de la mise à jour.");
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) resetState();
    onOpenChange(newOpen);
  };

  const STATUS_LABELS: Record<string, string> = {
    idea: 'Idée',
    analysis: 'Analyse',
    planned: 'Planifié',
    dev: 'Dev',
    review: 'Review',
    qa: 'QA',
    prod: 'Prod',
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        {step === 'form' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                Nouvelle Idée
              </DialogTitle>
              <DialogDescription>
                Décris ton idée — elle sera ajoutée au sandbox. L'analyse IA se lancera quand tu la déplaceras dans "Analyse".
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="feature-description">Description *</Label>
                <Textarea
                  id="feature-description"
                  placeholder="Décris en détail ce que tu voudrais, pourquoi c'est utile, et comment tu imagines la fonctionnalité..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={5}
                />
              </div>

              <div className="space-y-2">
                <Label>Urgence</Label>
                <RadioGroup value={priority} onValueChange={setPriority} className="grid grid-cols-3 gap-2">
                  {PRIORITY_OPTIONS.map((opt) => {
                    const Icon = opt.icon;
                    const isSelected = priority === opt.value;
                    return (
                      <Label
                        key={opt.value}
                        htmlFor={`priority-${opt.value}`}
                        className={cn(
                          'flex flex-col items-center gap-1 rounded-lg border p-3 cursor-pointer transition-all text-center',
                          isSelected
                            ? 'border-primary bg-primary/5 ring-1 ring-primary'
                            : 'border-border hover:bg-muted/50'
                        )}
                      >
                        <RadioGroupItem value={opt.value} id={`priority-${opt.value}`} className="sr-only" />
                        <Icon className={cn('h-4 w-4', opt.color)} />
                        <span className="text-xs font-medium">{opt.label}</span>
                        <span className="text-[10px] text-muted-foreground">{opt.description}</span>
                      </Label>
                    );
                  })}
                </RadioGroup>
              </div>
            </div>

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isSearching}>
                Annuler
              </Button>
              <Button onClick={handleCheckAndSubmit} disabled={!description.trim() || isSearching}>
                {isSearching ? (
                  <>
                    <Search className="mr-2 h-4 w-4 animate-pulse" />
                    Vérification...
                  </>
                ) : (
                  'Ajouter au sandbox'
                )}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'duplicates' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-warning" />
                Idées similaires trouvées
              </DialogTitle>
              <DialogDescription>
                Des idées existantes ressemblent à la tienne. Tu peux compléter l'une d'elles avec tes nouveaux éléments, ou créer une nouvelle idée quand même.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {similarIdeas.map((idea) => (
                <button
                  key={idea.id}
                  onClick={() => setSelectedComplementId(selectedComplementId === idea.id ? null : idea.id)}
                  className={cn(
                    'w-full text-left rounded-lg border p-3 transition-all',
                    selectedComplementId === idea.id
                      ? 'border-primary bg-primary/5 ring-1 ring-primary'
                      : 'border-border hover:bg-muted/50'
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{idea.title}</p>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{idea.description}</p>
                    </div>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground shrink-0">
                      {STATUS_LABELS[idea.status] || idea.status}
                    </span>
                  </div>
                </button>
              ))}
            </div>

            <DialogFooter className="mt-4 flex-col gap-2 sm:flex-col">
              {selectedComplementId && (
                <Button
                  onClick={() => handleComplement(selectedComplementId)}
                  disabled={isSubmitting}
                  className="w-full"
                >
                  {isSubmitting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="mr-2 h-4 w-4" />
                  )}
                  Compléter cette idée
                </Button>
              )}
              <div className="flex gap-2 w-full">
                <Button variant="outline" onClick={() => setStep('form')} disabled={isSubmitting} className="flex-1">
                  Retour
                </Button>
                <Button
                  variant="secondary"
                  onClick={doSubmit}
                  disabled={isSubmitting}
                  className="flex-1"
                >
                  {isSubmitting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <ArrowRight className="mr-2 h-4 w-4" />
                  )}
                  Créer quand même
                </Button>
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
